import { useMemo } from 'react'
import {
  isKnownItemType,
  itemContentSchemas,
  multiChoiceAnswerSchema,
  singleChoiceAnswerSchema,
  type MultiChoiceContent,
  type SingleChoiceContent,
} from '@sciolingo/api-client'
import type { TaskRendererProps } from './types'
import { ChoiceItem } from './items/ChoiceItem'
import { UnsupportedItem } from './items/UnsupportedItem'

/**
 * Given an item, render it and emit answer events. That is the whole job: no
 * navigation, timer, fetch or persistence.
 *
 * §7.4 governs the two failure modes: an unknown `type` and content that does
 * not match its schema both degrade to an explicit placeholder with a logged
 * warning. Neither ever throws — a malformed item must not be able to end a
 * student's session, and content must never be able to define client behaviour.
 */
export function TaskRenderer(props: TaskRendererProps) {
  const { item, initialAnswer, disabled = false, onAnswerChange, onInteraction } = props

  const parsed = useMemo(() => parseContent(item.type, item.content), [item.type, item.content])

  if (!parsed.ok) {
    return <UnsupportedItem itemId={item.id} type={item.type} reason={parsed.reason} />
  }

  switch (parsed.type) {
    case 'single-choice':
      return (
        <ChoiceItem
          key={item.id}
          mode="single"
          content={parsed.content as SingleChoiceContent}
          initialAnswer={safeParse(singleChoiceAnswerSchema, initialAnswer)}
          disabled={disabled}
          onAnswerChange={onAnswerChange}
          onInteraction={onInteraction}
        />
      )

    case 'multi-choice':
      return (
        <ChoiceItem
          key={item.id}
          mode="multi"
          content={parsed.content as MultiChoiceContent}
          initialAnswer={safeParse(multiChoiceAnswerSchema, initialAnswer)}
          disabled={disabled}
          onAnswerChange={onAnswerChange}
          onInteraction={onInteraction}
        />
      )

    // numeric, ordering and passage-group are not implemented yet.
    // Until they are, they take the same graceful path as an unknown type rather
    // than a half-working one, so a session never silently records them wrongly.
    default:
      return <UnsupportedItem itemId={item.id} type={item.type} reason="not-yet-implemented" />
  }
}

type ParseOutcome =
  | { ok: true; type: string; content: unknown }
  | { ok: false; reason: 'unknown-type' | 'invalid-content' }

function parseContent(type: string, content: unknown): ParseOutcome {
  if (!isKnownItemType(type)) {
    console.warn(`[renderer] skipping item of unknown type "${type}" (spec §7.4)`)
    return { ok: false, reason: 'unknown-type' }
  }

  const result = itemContentSchemas[type].safeParse(content)
  if (!result.success) {
    console.warn(`[renderer] content failed validation for type "${type}"`, result.error.issues)
    return { ok: false, reason: 'invalid-content' }
  }

  return { ok: true, type, content: result.data }
}

function safeParse<T>(schema: { safeParse(v: unknown): { success: boolean; data?: unknown } }, value: unknown): T | undefined {
  if (value === undefined || value === null) return undefined
  const result = schema.safeParse(value)
  return result.success ? (result.data as T) : undefined
}
